// src/components/ExtractedData.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { 
  Download, 
  FileText, 
  ArrowLeft, 
  RefreshCw, 
  CheckCircle, 
  AlertCircle,
  Copy,
  Eye,
  EyeOff
} from 'lucide-react';
import Button from './Button';

const FIELDS = [
  "Halle",
  "Rechnungsersteller",
  "Art",
  "Leistung",
  "Rechnungsnummer",
  "Rechnungsdatum",
  "Nettobetrag",
  "Bruttobetrag"
];

export default function ExtractedData() {
  const location = useLocation();
  const navigate = useNavigate();
  const results = location.state?.results || {};
  const fromHistory = location.state?.fromHistory || false;
  const rows = Object.entries(results);
  const [showRawData, setShowRawData] = useState(false);
  const [copied, setCopied] = useState(false);

  const downloadJSON = () => {
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `extraction_results_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(results, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const getSuccessCount = () => {
    return rows.filter(([_, obj]) => !obj?.error).length;
  };

  const getErrorCount = () => {
    return rows.filter(([_, obj]) => obj?.error).length;
  };

  if (!rows.length) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Results Found</h3>
          <p className="text-gray-600 mb-6">No extraction results to display. Please process some PDFs first.</p>
          <div className="flex justify-center space-x-4">
            <Button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700 text-white">
              <FileText className="w-4 h-4 mr-2" />
              Upload PDFs
            </Button>
            <Button onClick={() => navigate('/history')} className="bg-gray-600 hover:bg-gray-700 text-white">
              <History className="w-4 h-4 mr-2" />
              View History
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => fromHistory ? navigate('/history') : navigate('/')}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{fromHistory ? 'Back to History' : 'Back to Upload'}</span>
          </button>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Extraction Results</h2>
            <p className="text-gray-600">
              {rows.length} file{rows.length !== 1 ? 's' : ''} processed
              {fromHistory && ' • From History'}
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => setShowRawData(!showRawData)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700"
          >
            {showRawData ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {showRawData ? 'Hide' : 'Show'} Raw Data
          </Button>
          <Button
            onClick={copyToClipboard}
            className="bg-blue-100 hover:bg-blue-200 text-blue-700"
          >
            <Copy className="w-4 h-4 mr-2" />
            {copied ? 'Copied!' : 'Copy'}
          </Button>
          <Button onClick={downloadJSON} className="bg-green-600 hover:bg-green-700 text-white">
            <Download className="w-4 h-4 mr-2" />
            Download JSON
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Files</p>
              <p className="text-2xl font-bold text-gray-900">{rows.length}</p>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Successful</p>
              <p className="text-2xl font-bold text-green-600">{getSuccessCount()}</p>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Errors</p>
              <p className="text-2xl font-bold text-red-600">{getErrorCount()}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Raw Data Toggle */}
      {showRawData && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-gray-900 rounded-lg p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-4">Raw JSON Data</h3>
          <pre className="text-green-400 text-sm overflow-x-auto">
            {JSON.stringify(results, null, 2)}
          </pre>
        </motion.div>
      )}

      {/* Results Table */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Extracted Data</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File
                </th>
                {FIELDS.map((field) => (
                  <th key={field} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {field}
                  </th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map(([name, obj], index) => (
                <motion.tr 
                  key={name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FileText className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">{name}</span>
                    </div>
                  </td>
                  {FIELDS.map((field) => (
                    <td key={field} className="px-6 py-4 text-sm text-gray-900">
                      {obj?.[field] || '-'}
                    </td>
                  ))}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {obj?.error ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Error
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Success
                      </span>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Error Details */}
      {getErrorCount() > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-red-800 mb-4">Error Details</h4>
          <div className="space-y-2">
            {rows
              .filter(([_, obj]) => obj?.error)
              .map(([name, obj]) => (
                <div key={name} className="text-sm">
                  <span className="font-medium text-red-700">{name}:</span>
                  <span className="text-red-600 ml-2">{obj.error}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-center space-x-4">
        <Button onClick={() => navigate('/')} className="bg-[#175e69] hover:bg-[#175e69]/90 text-white">
          <FileText className="w-4 h-4 mr-2" />
          Process More PDFs
        </Button>
        <Button onClick={() => window.location.reload()} className="bg-[#2a7c8a] hover:bg-[#2a7c8a]/90 text-white">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Results
        </Button>
      </div>
    </motion.div>
  );
}

